// MediaLibraryView.jsx — View PURA da BIBLIOTECA DE IMAGENS (janela por cima do editor de página).
// Recebe { vm, actions }. Contrato: mediaLibrary.vm.js (@version 1). Sem fetch, sem regra de negócio:
// rótulos, contadores, permissões (selectable/canDelete/canDescribe/canUpload) e mensagens vêm prontos.
// `useState` só de UI: qual foto está ampliada, se há arquivo sendo arrastado por cima, miniatura carregada.
//
// ═══════════════════════════════════════════════════════════════════════════════
// A DECISÃO DE DESENHO: UMA PAREDE DE FOTOS, NÃO UMA LISTA DE ARQUIVOS
//
//   GRADE JUSTIFICADA — cada miniatura cresce na proporção do `aspectRatio` dela (flex-grow = ar,
//     flex-basis = ar × altura da linha). Todas as fotos de uma linha ficam com a MESMA altura e a
//     linha fecha a largura toda: parece portfólio, reflui sozinha de 360 px a 1200 px, e como o
//     espaço é reservado pela proporção (com `color` de fundo) nada pula quando a foto chega.
//   ESCOLHER — tocar é escolher. A foto escolhida recua para dentro de uma moldura na cor da marca
//     e ganha o NÚMERO no canto (a ordem da página). Nunca só cor: número + moldura + recuo.
//     O rodapé mostra a fila escolhida crescendo, na ordem, ao lado do botão de confirmar.
//   AMPLIAR — botão próprio em cada foto (tocar já é escolher). A foto grande traz o crédito do
//     fotógrafo (exemplos do Unsplash pedem), a descrição e as ações de descrever/apagar.
//   ENVIAR — arrastar para qualquer lugar da janela, ou o botão. Cada envio é uma linha que conta a
//     sua etapa: na fila (foto apagada) → preparando → enviando (a foto vai "revelando" de baixo
//     para cima com o progresso) → processando → pronta. Repetida = aviso gentil, não erro.
//   CONTA NOVA — "Suas fotos" vira um convite grande para enviar, e os exemplos logo abaixo são a
//     boa notícia: dá para montar a galeria hoje.
//
// Consistência: CARD_SX, TINT, botões (PRIMARY/OUTLINE/QUIET/DANGER), StateBlock, NoteRow e
// diálogos com o mesmo desenho de Páginas/Serviços/Configurações. Neutros vêm de --bg-page/--divider/
// --text-*; nunca da escala --slate-* (quebraria temas escuros). A única cor que não é token é
// `item.color` — dado da foto.
import { useRef, useState } from 'react';
import {
  Box, Typography, Button, ButtonBase, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Skeleton, CircularProgress, LinearProgress, TextField,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import OpenInFullRounded from '@mui/icons-material/OpenInFullRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PhotoLibraryRounded from '@mui/icons-material/PhotoLibraryRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import HourglassEmptyRounded from '@mui/icons-material/HourglassEmptyRounded';
import ImageNotSupportedRounded from '@mui/icons-material/ImageNotSupportedRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import AccessibilityNewRounded from '@mui/icons-material/AccessibilityNewRounded';
import BrokenImageRounded from '@mui/icons-material/BrokenImageRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (mesmos papéis das outras telas)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
  brand: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)', sub: 'var(--color-primary-dark)', main: 'var(--color-primary)' },
};
const tint = (tone) => TINT[tone] || TINT.neutral;

const HEAD = { fontWeight: 'var(--fw-bold)' };
const FOCUS = { outline: '2px solid var(--color-primary)', outlineOffset: 2 };

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 44,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: 0.6 },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const BRAND_OUTLINE_BTN_SX = {
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)', textTransform: 'none', whiteSpace: 'nowrap',
  border: '1px solid', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)',
  fontWeight: 'var(--fw-bold)', bgcolor: 'var(--surface)',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { borderColor: 'var(--divider)', color: 'var(--text-disabled)' },
  '&:focus-visible': FOCUS,
};

const OUTLINE_BTN_SX = {
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  border: '1px solid', borderColor: 'var(--divider)',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': FOCUS,
};

const QUIET_BTN_SX = {
  minHeight: 42, px: 1.5, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  '&:focus-visible': FOCUS,
};

const DANGER_BTN_SX = {
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  bgcolor: 'var(--color-error)', color: 'var(--on-primary)', fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--tint-error-fg)' },
  '&.Mui-disabled': { bgcolor: 'var(--tint-error-bg)', color: 'var(--tint-error-sub)' },
  '&:focus-visible': { outline: '2px solid var(--color-error)', outlineOffset: 2 },
};

const DANGER_QUIET_BTN_SX = {
  ...QUIET_BTN_SX,
  color: 'var(--color-error)',
  '&:hover': { bgcolor: 'var(--tint-error-bg)', color: 'var(--color-error)' },
  '&:focus-visible': { outline: '2px solid var(--color-error)', outlineOffset: 2 },
};

const ICON_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': FOCUS,
};

// Botão redondo sobre a foto: superfície quase opaca, legível em foto clara ou escura, nos dois modos.
const ON_PHOTO_BTN_SX = {
  width: 40, height: 40,
  bgcolor: 'color-mix(in srgb, var(--surface) 88%, transparent)',
  color: 'var(--text-primary)', boxShadow: 'var(--shadow-card)',
  '&:hover': { bgcolor: 'var(--surface)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 1, opacity: 1 },
};

const DIALOG_PAPER_SX = {
  bgcolor: 'var(--surface)', color: 'var(--text-primary)', backgroundImage: 'none',
  borderRadius: 'var(--radius-card)', border: '1px solid', borderColor: 'var(--divider)',
  boxShadow: 'var(--shadow-hover)',
};

const FIELD_SX = {
  '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--color-primary-dark)' },
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 'var(--fs-body2)', bgcolor: 'var(--surface)',
    '& fieldset': { borderColor: 'var(--divider)' },
    '&:hover fieldset': { borderColor: 'var(--color-primary-light)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
    '&.Mui-error fieldset': { borderColor: 'var(--color-error)' },
  },
  '& .MuiFormHelperText-root': { color: 'var(--text-secondary)', mx: 0 },
};

const SK = { bgcolor: 'var(--divider)' };
const REDUCED = { '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' } };

const pop = keyframes`
  0% { transform: scale(.3); opacity: 0; }
  60% { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1); }
`;
const breathe = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .35; }
`;
const sweep = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;
const rise = keyframes`
  from { transform: translateY(8px); opacity: 0; }
  to { transform: none; opacity: 1; }
`;

/* Altura da linha da grade justificada (px), por largura da janela. */
const ROW_H = { xs: 128, sm: 164, lg: 188 };
const ratioOf = (ar) => (ar > 0 ? Math.min(Math.max(ar, 0.5), 2.4) : 1);
const tileFlex = (ar) => {
  const r = ratioOf(ar);
  return {
    flexGrow: r, flexShrink: 1, aspectRatio: String(r), minWidth: 0,
    flexBasis: { xs: `${r * ROW_H.xs}px`, sm: `${r * ROW_H.sm}px`, lg: `${r * ROW_H.lg}px` },
  };
};

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas
   ═══════════════════════════════════════════════════════════════════════════ */

function NoteRow({ tone = 'info', icon, children, role, sx }) {
  const t = tint(tone);
  return (
    <Box role={role} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: t.bg, border: '1px solid', borderColor: t.border, borderRadius: 'var(--radius-sm)', px: 1.5, py: 1.25, ...sx }}>
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg, mt: '1px', flexShrink: 0 }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, color: t.fg }}>{children}</Box>
    </Box>
  );
}

function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Typography variant="subtitle1" component="h3" sx={{ ...HEAD, color: 'var(--text-primary)', mb: 0.5 }}>{title}</Typography>
      {description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 420 }}>{description}</Typography>}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/** Pastilha sobre a foto: superfície opaca → legível em qualquer foto, nos dois modos. */
function PhotoChip({ icon, label, tone }) {
  const brand = tone === 'brand';
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', minWidth: 0,
        bgcolor: 'var(--surface)', color: brand ? 'var(--color-primary-dark)' : 'var(--text-primary)',
        borderRadius: 999, px: 0.875, py: '2px', boxShadow: 'var(--shadow-card)',
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>{icon}</Box>}
      <Typography variant="caption" sx={{ fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.5 }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Pastilha de informação (painel da foto ampliada). */
function InfoChip({ tone = 'neutral', icon, label }) {
  const t = tint(tone);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.625, maxWidth: '100%', bgcolor: t.bg, border: '1px solid', borderColor: t.border, borderRadius: 999, px: 1.125, py: '3px' }}>
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg }}>{icon}</Box>}
      <Typography variant="caption" sx={{ color: t.fg, fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Typography>
    </Box>
  );
}

/** O número da ordem (ou o "check", no logo). Entra com um pulo curto. */
function OrderBadge({ index, size = 30 }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 0 0 2px var(--surface), var(--shadow-card)',
        animation: `${pop} .32s cubic-bezier(.2,.9,.3,1.3)`, ...REDUCED,
      }}
    >
      {index != null
        ? <Typography variant="subtitle2" component="span" sx={{ ...HEAD, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: 'inherit' }}>{index}</Typography>
        : <CheckRounded sx={{ fontSize: Math.round(size * 0.62) }} />}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A MINIATURA — tocar escolhe; o botão do canto amplia
   ═══════════════════════════════════════════════════════════════════════════ */

function PhotoTile({ item, onToggle, onExpand }) {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const name = item.alt || item.name || 'Foto';
  const locked = !item.selectable && !item.selected;
  const disabled = locked || item.busy;
  const state = item.selected
    ? (item.selectionIndex != null ? `escolhida, número ${item.selectionIndex} na ordem` : 'escolhida')
    : (item.addedLabel || (locked ? 'indisponível' : 'não escolhida'));
  const corner = item.addedLabel || item.usedInLabel;

  return (
    <Box
      sx={{
        ...tileFlex(item.aspectRatio), position: 'relative',
        borderRadius: 'var(--radius-sm)',
        bgcolor: item.selected ? 'var(--color-primary-soft)' : 'transparent',
        transition: 'background-color .2s',
        '& .tileHover': { opacity: 0, transition: 'opacity .15s' },
        '&:hover .tileHover, &:focus-within .tileHover': { opacity: 1 },
        '@media (hover: none)': { '& .tileHover': { opacity: 1 } },
      }}
    >
      <ButtonBase
        focusRipple
        disabled={disabled}
        onClick={() => onToggle(item.id)}
        role="checkbox"
        aria-checked={!!item.selected}
        aria-disabled={disabled || undefined}
        aria-label={`${name} — ${state}`}
        sx={{
          position: 'absolute', inset: 0, display: 'block', borderRadius: 'inherit',
          boxShadow: item.selected ? 'inset 0 0 0 3px var(--color-primary)' : 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          '&.Mui-disabled': { pointerEvents: 'auto' },
          '&.Mui-focusVisible': { outline: '3px solid var(--color-primary-dark)', outlineOffset: 2 },
          '&:hover .tileImg': !disabled && !item.selected ? { transform: 'scale(1.035)' } : {},
        }}
      >
        <Box
          sx={{
            position: 'absolute', inset: 0, overflow: 'hidden',
            borderRadius: item.selected ? 'calc(var(--radius-sm) - 3px)' : 'var(--radius-sm)',
            bgcolor: item.color || 'var(--divider)',
            transform: item.selected ? 'scale(.86)' : 'none',
            transition: 'transform .24s cubic-bezier(.2,.8,.2,1), border-radius .24s', ...REDUCED,
          }}
        >
          {!broken ? (
            <Box
              component="img"
              className="tileImg"
              src={item.thumbnailUrl}
              alt={item.alt || item.name || ''}
              loading="lazy"
              decoding="async"
              draggable={false}
              onLoad={() => setLoaded(true)}
              onError={() => setBroken(true)}
              sx={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                opacity: loaded ? (locked ? 0.42 : 1) : 0,
                filter: locked ? 'grayscale(.5)' : 'none',
                transition: 'opacity .35s, transform .3s', ...REDUCED,
              }}
            />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--bg-page)' }}>
              <BrokenImageRounded sx={{ fontSize: 28, color: 'var(--text-secondary)' }} />
            </Box>
          )}
        </Box>

        {/* canto de cima: número (escolhida) ou o aro vazio que convida a tocar */}
        <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex' }}>
          {item.selected ? (
            <OrderBadge index={item.selectionIndex} />
          ) : !disabled ? (
            <Box
              className="tileHover"
              aria-hidden="true"
              sx={{
                width: 26, height: 26, borderRadius: '50%', m: '2px',
                border: '2px solid var(--surface)',
                bgcolor: 'color-mix(in srgb, var(--surface) 30%, transparent)',
                boxShadow: 'var(--shadow-card)',
              }}
            />
          ) : null}
        </Box>

        {/* canto de baixo: já está nesta galeria / logo atual / em uso */}
        {corner && (
          <Box sx={{ position: 'absolute', left: 8, right: 8, bottom: 8, display: 'flex' }}>
            <PhotoChip
              label={corner}
              icon={item.addedLabel ? <CheckRounded sx={{ fontSize: 13 }} /> : <LayersRounded sx={{ fontSize: 13 }} />}
            />
          </Box>
        )}

        {item.busy && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'color-mix(in srgb, var(--surface) 60%, transparent)', borderRadius: 'inherit' }}>
            <CircularProgress size={26} sx={{ color: 'var(--color-primary)' }} aria-label="Apagando" />
          </Box>
        )}
      </ButtonBase>

      {!item.busy && (
        <Tooltip title="Ampliar">
          <IconButton
            className="tileHover"
            aria-label={`Ampliar ${name}`}
            onClick={() => onExpand(item.id)}
            sx={{ ...ON_PHOTO_BTN_SX, position: 'absolute', top: 4, right: 4 }}
          >
            <OpenInFullRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

/** A linha de fotos: justificada; o "enchimento" final impede a última linha de esticar. */
function JustifiedRow({ children }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: { xs: 0.75, sm: 1 } }}>
      {children}
      <Box aria-hidden="true" sx={{ flexGrow: 1000, flexBasis: 0, height: 0 }} />
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENVIAR — o ladrilho de envio, o convite da conta nova e a fila de envios
   ═══════════════════════════════════════════════════════════════════════════ */

function UploadTile({ upload, onPick }) {
  if (!upload.canUpload) {
    return (
      <Box sx={{ ...tileFlex(1.5), position: 'relative' }}>
        <Box
          role="note"
          sx={{
            position: 'absolute', inset: 0, borderRadius: 'var(--radius-sm)',
            bgcolor: 'var(--tint-warning-bg)', border: '1px dashed', borderColor: 'var(--tint-warning-border)',
            display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75, p: 1.5, overflow: 'hidden',
          }}
        >
          <LockOutlined sx={{ fontSize: 22, color: 'var(--tint-warning-fg)' }} />
          <Typography variant="caption" sx={{ color: 'var(--tint-warning-fg)', lineHeight: 1.4, textWrap: 'pretty' }}>
            {upload.blockedReason}
          </Typography>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ ...tileFlex(1.1), position: 'relative' }}>
      <ButtonBase
        focusRipple
        onClick={onPick}
        aria-label={`${upload.buttonLabel} — ${upload.acceptLabel}`}
        sx={{
          position: 'absolute', inset: 0, borderRadius: 'var(--radius-sm)',
          border: '2px dashed', borderColor: 'var(--color-primary-light)', bgcolor: 'var(--color-primary-soft)',
          display: 'flex', flexDirection: 'column', gap: 0.75, p: 1, textAlign: 'center',
          transition: 'border-color .15s, background-color .15s',
          '&:hover': { borderColor: 'var(--color-primary)' },
          '&.Mui-focusVisible': { outline: '3px solid var(--color-primary-dark)', outlineOffset: 2 },
        }}
      >
        <CloudUploadRounded sx={{ fontSize: { xs: 26, sm: 32 }, color: 'var(--color-primary-dark)' }} />
        <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'var(--color-primary-dark)', lineHeight: 1.2 }}>
          {upload.buttonLabel}
        </Typography>
        <Typography variant="caption" component="span" sx={{ color: 'var(--text-secondary)', lineHeight: 1.3, display: { xs: 'none', sm: 'block' } }}>
          {upload.dropLabel}
        </Typography>
      </ButtonBase>
    </Box>
  );
}

/** Conta nova (ou sem nenhuma foto): "Suas fotos" vira o convite — e aponta para os exemplos. */
function EmptyOwner({ section, upload, onPick, examplesTitle, onSeeExamples }) {
  return (
    <Box
      sx={{
        border: '2px dashed', borderColor: upload.canUpload ? 'var(--color-primary-light)' : 'var(--divider)',
        bgcolor: 'var(--color-primary-soft)', borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
        px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 4 },
        display: 'grid', gap: { xs: 2, md: 4 }, alignItems: 'center',
        gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr) auto' },
      }}
    >
      <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-card)' }}>
        <CloudUploadRounded sx={{ fontSize: 36, color: 'var(--color-primary-dark)' }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body1" sx={{ color: 'var(--text-primary)', lineHeight: 1.5, textWrap: 'pretty', maxWidth: 560 }}>
          {section.emptyMessage}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mt: 0.5 }}>
          {upload.acceptLabel}
        </Typography>
        {examplesTitle && (
          <Button
            onClick={onSeeExamples}
            endIcon={<ArrowDownwardRounded sx={{ fontSize: 18 }} />}
            sx={{ ...QUIET_BTN_SX, color: 'var(--color-primary-dark)', px: 1, ml: -1, mt: 0.5, '&:hover': { bgcolor: 'var(--surface)' } }}
          >
            Ver os exemplos
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'center' }, gap: 0.5 }}>
        {upload.canUpload ? (
          <>
            <Button onClick={onPick} startIcon={<CloudUploadRounded />} disableElevation sx={PRIMARY_BTN_SX}>{upload.buttonLabel}</Button>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>{upload.dropLabel}</Typography>
          </>
        ) : (
          <NoteRow tone="warning" icon={<LockOutlined sx={{ fontSize: 18 }} />}>
            <Typography variant="body2">{upload.blockedReason}</Typography>
          </NoteRow>
        )}
      </Box>
    </Box>
  );
}

const STAGE_ORDER = { waiting: 0, preparing: 1, uploading: 2, processing: 3, done: 4 };

/** As três etapas como trilho: cheio = feito, pulsando = agora. "Enviando" enche com o progresso. */
function StageTrack({ up }) {
  const s = STAGE_ORDER[up.state] ?? 0;
  const done = up.state === 'done';
  const seg = (i) => {
    const full = s > i + 1 || done;
    const now = s === i + 1 && !done;
    const fill = full ? 100 : now && i === 1 ? up.progress : now ? 100 : 0;
    return (
      <Box key={i} sx={{ flex: 1, height: 4, borderRadius: 999, bgcolor: 'var(--divider)', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%', width: `${fill}%`, borderRadius: 999,
            bgcolor: done ? 'var(--color-success)' : 'var(--color-primary)',
            transition: 'width .35s ease',
            ...(now && i !== 1 ? { animation: `${breathe} 1.2s ease-in-out infinite` } : {}),
            ...(now && i === 2 ? {
              backgroundImage: 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-light) 50%, var(--color-primary) 100%)',
              backgroundSize: '200% 100%', animation: `${sweep} 1.4s linear infinite`,
            } : {}),
            ...REDUCED,
          }}
        />
      </Box>
    );
  };
  return <Box aria-hidden="true" sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>{[0, 1, 2].map(seg)}</Box>;
}

function UploadThumb({ up }) {
  const waiting = up.state === 'waiting';
  const uploading = up.state === 'uploading';
  return (
    <Box sx={{ position: 'relative', width: 56, height: 56, flexShrink: 0, borderRadius: 'var(--radius-sm)', overflow: 'hidden', bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)' }}>
      {up.previewUrl ? (
        <Box
          component="img" src={up.previewUrl} alt="" draggable={false}
          sx={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            filter: waiting ? 'grayscale(1)' : up.state === 'preparing' ? 'blur(1.5px)' : 'none',
            opacity: waiting ? 0.55 : 1, transition: 'filter .3s, opacity .3s',
          }}
        />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ImageNotSupportedRounded sx={{ fontSize: 24, color: up.tone === 'error' ? 'var(--color-error)' : 'var(--text-secondary)' }} />
        </Box>
      )}
      {/* enviando: a foto "revela" de baixo para cima conforme o progresso */}
      {uploading && (
        <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: `${100 - (up.progress || 0)}%`, bgcolor: 'color-mix(in srgb, var(--surface) 72%, transparent)', transition: 'height .35s ease', borderBottom: '2px solid var(--color-primary)' }} />
      )}
      {up.state === 'waiting' && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <HourglassEmptyRounded sx={{ fontSize: 20, color: 'var(--text-primary)' }} />
        </Box>
      )}
      {up.state === 'processing' && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'color-mix(in srgb, var(--surface) 40%, transparent)' }}>
          <CircularProgress size={22} thickness={5} sx={{ color: 'var(--color-primary)' }} />
        </Box>
      )}
      {up.state === 'done' && (
        <Box sx={{ position: 'absolute', right: 3, bottom: 3, width: 20, height: 20, borderRadius: '50%', bgcolor: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: `${pop} .3s`, ...REDUCED }}>
          <CheckCircleRounded sx={{ fontSize: 20, color: 'var(--color-success)' }} />
        </Box>
      )}
    </Box>
  );
}

function UploadRow({ up, onRetry, onDismiss }) {
  const isError = up.state === 'error';
  const isDup = up.state === 'duplicate';
  const isDone = up.state === 'done';
  const inFlight = !isError && !isDup && !isDone;
  const t = isError ? TINT.error : isDup ? TINT.info : null;
  const statusColor = isError ? 'var(--tint-error-fg)' : isDup ? 'var(--tint-info-fg)' : isDone ? 'var(--tint-success-fg)' : 'var(--text-secondary)';

  return (
    <Box
      component="li"
      sx={{
        gridColumn: isError ? '1 / -1' : 'auto',
        display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0,
        p: 1, borderRadius: 'var(--radius-sm)',
        bgcolor: t ? t.bg : 'var(--surface)', border: '1px solid', borderColor: t ? t.border : 'var(--divider)',
        animation: `${rise} .25s ease-out`, ...REDUCED,
      }}
    >
      <UploadThumb up={up} />
      <Box sx={{ minWidth: 0, flex: 1, pt: '2px' }}>
        <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {up.fileName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: '2px' }}>
          {isError && <ErrorRounded sx={{ fontSize: 16, color: 'var(--color-error)', mt: '1px', flexShrink: 0 }} />}
          {isDup && <InfoOutlined sx={{ fontSize: 16, color: 'var(--tint-info-fg)', mt: '1px', flexShrink: 0 }} />}
          {isDone && <CheckRounded sx={{ fontSize: 16, color: 'var(--color-success)', mt: '1px', flexShrink: 0 }} />}
          <Typography variant="caption" sx={{ color: statusColor, lineHeight: 1.45, fontWeight: inFlight ? 'var(--fw-medium)' : 'var(--fw-regular)', textWrap: 'pretty', fontVariantNumeric: 'tabular-nums' }}>
            {up.statusLabel}
          </Typography>
        </Box>
        {(inFlight || isDone) && <StageTrack up={up} />}
      </Box>
      {(up.canRetry || up.canDismiss) && (
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          {up.canRetry && (
            <Button onClick={() => onRetry(up.id)} startIcon={<ReplayRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, minHeight: 40, px: 1.5, bgcolor: 'var(--surface)' }}>
              Tentar de novo
            </Button>
          )}
          {up.canDismiss && (
            <Tooltip title="Tirar da lista">
              <IconButton aria-label={`Tirar ${up.fileName} da lista`} onClick={() => onDismiss(up.id)} sx={ICON_BTN_SX}>
                <CloseRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}

function UploadTray({ uploads, a }) {
  if (!uploads?.length) return null;
  return (
    <Box component="section" aria-label="Envios" sx={{ ...CARD_SX, bgcolor: 'var(--bg-page)', boxShadow: 'none', p: { xs: 1.25, sm: 1.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, px: 0.25 }}>
        <CloudUploadRounded sx={{ fontSize: 18, color: 'var(--color-primary-dark)' }} />
        <Typography variant="overline" component="h3" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em', lineHeight: 1.6 }}>
          Envios
        </Typography>
      </Box>
      <Box
        component="ul"
        aria-live="polite"
        sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(300px, 1fr))' } }}
      >
        {uploads.map((up) => (
          <UploadRow key={up.id} up={up} onRetry={(id) => a.onRetryUpload?.(id)} onDismiss={(id) => a.onDismissUpload?.(id)} />
        ))}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Cabeçalho: cota e botão de enviar
   ═══════════════════════════════════════════════════════════════════════════ */

function QuotaMeter({ quota }) {
  if (!quota) return null;
  const bar = quota.level === 'ok' ? 'var(--color-primary)' : 'var(--color-warning)';
  const hintColor = quota.level === 'full' ? 'var(--tint-warning-fg)' : 'var(--text-secondary)';
  return (
    <Box sx={{ minWidth: 0, flex: { xs: '1 1 auto', md: '0 1 220px' }, maxWidth: { md: 240 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
        <PhotoLibraryRounded sx={{ fontSize: 16, color: 'var(--text-secondary)' }} />
        <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {quota.label}
        </Typography>
      </Box>
      {quota.percent != null && (
        <LinearProgress
          variant="determinate"
          value={quota.percent}
          aria-label={quota.label}
          sx={{ mt: 0.5, height: 5, borderRadius: 999, bgcolor: 'var(--divider)', '& .MuiLinearProgress-bar': { bgcolor: bar, borderRadius: 999 } }}
        />
      )}
      {quota.hint && (
        <Typography variant="caption" sx={{ display: 'block', color: hintColor, mt: 0.5, lineHeight: 1.35 }}>
          {quota.hint}
        </Typography>
      )}
    </Box>
  );
}

function UploadButton({ upload, onPick }) {
  const btn = (
    <Button
      onClick={onPick}
      disabled={!upload.canUpload}
      startIcon={upload.canUpload ? <CloudUploadRounded /> : <LockOutlined />}
      sx={BRAND_OUTLINE_BTN_SX}
    >
      {upload.buttonLabel}
    </Button>
  );
  if (upload.canUpload) return btn;
  return <Tooltip title={upload.blockedReason || ''}><span>{btn}</span></Tooltip>;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Seções
   ═══════════════════════════════════════════════════════════════════════════ */

function SectionHead({ section }) {
  const examples = section.key === 'examples';
  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {examples
          ? <AutoAwesomeRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />
          : <PhotoLibraryRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />}
        <Typography variant="subtitle1" component="h3" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{section.title}</Typography>
        {section.countLabel && (
          <Box sx={{ px: 0.875, borderRadius: 999, bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)' }}>
            <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{section.countLabel}</Typography>
          </Box>
        )}
      </Box>
      {section.subtitle && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.25, textWrap: 'pretty' }}>{section.subtitle}</Typography>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Esqueleto — já com o ritmo da grade justificada
   ═══════════════════════════════════════════════════════════════════════════ */

const SKELETON_RATIOS = [
  [0.67, 1.5, 1.5, 0.8, 1.33, 0.75],
  [1.5, 0.67, 1.25, 1.5, 0.8, 1.5, 0.7],
  [0.8, 1.5, 0.67, 1.33, 1.5, 0.75],
];

function LoadingSkeleton() {
  return (
    <Box aria-busy="true" aria-live="polite" aria-label="Carregando fotos" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {[0, 1].map((s) => (
        <Box key={s}>
          <Skeleton variant="text" width={s ? 200 : 120} height={28} sx={SK} />
          {s === 1 && <Skeleton variant="text" width="min(420px, 80%)" height={18} sx={{ ...SK, mb: 1 }} />}
          <JustifiedRow>
            {(s ? [...SKELETON_RATIOS[1], ...SKELETON_RATIOS[2]] : SKELETON_RATIOS[0]).map((r, i) => (
              <Box key={i} sx={{ ...tileFlex(r) }}>
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{ ...SK, width: '100%', height: '100%', borderRadius: 'var(--radius-sm)', '&::after': { animationDelay: `${i * 0.08}s` } }}
                />
              </Box>
            ))}
          </JustifiedRow>
        </Box>
      ))}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Rodapé — a escolha crescendo, na ordem, ao lado do confirmar
   ═══════════════════════════════════════════════════════════════════════════ */

function SelectionStrip({ items, onToggle }) {
  if (!items.length) return null;
  return (
    <Box
      component="ol"
      aria-label="Fotos escolhidas, na ordem"
      sx={{
        listStyle: 'none', m: 0, p: '4px', display: 'flex', gap: 0.75, overflowX: 'auto', minWidth: 0,
        scrollbarWidth: 'thin',
      }}
    >
      {items.map((it) => (
        <Box component="li" key={it.id} sx={{ flexShrink: 0, animation: `${rise} .22s ease-out`, ...REDUCED }}>
          <Tooltip title="Tirar da escolha">
            <ButtonBase
              focusRipple
              onClick={() => onToggle(it.id)}
              aria-label={`Tirar ${it.alt || it.name || 'foto'} da escolha`}
              sx={{
                position: 'relative', width: 44, height: 44, borderRadius: 'var(--radius-sm)', overflow: 'hidden',
                bgcolor: it.color || 'var(--divider)', boxShadow: '0 0 0 2px var(--color-primary)',
                '&.Mui-focusVisible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 3 },
                '&:hover .stripX': { opacity: 1 },
              }}
            >
              <Box component="img" src={it.thumbnailUrl} alt="" draggable={false} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <Box sx={{ position: 'absolute', top: 2, left: 2 }}>
                <OrderBadge index={it.selectionIndex} size={18} />
              </Box>
              <Box className="stripX" sx={{ position: 'absolute', inset: 0, opacity: 0, transition: 'opacity .15s', bgcolor: 'color-mix(in srgb, var(--surface) 70%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CloseRounded sx={{ fontSize: 20, color: 'var(--text-primary)' }} />
              </Box>
            </ButtonBase>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A FOTO AMPLIADA — crédito, descrição, escolher, descrever, apagar
   ═══════════════════════════════════════════════════════════════════════════ */

function Lightbox({ items, index, mode, fullScreen, onClose, onStep, a }) {
  const item = items[index];
  if (!item) return null;
  const r = ratioOf(item.aspectRatio);
  const canPrev = index > 0;
  const canNext = index < items.length - 1;
  const title = item.alt || item.name || 'Foto';
  const selectLabel = item.selected ? 'Tirar da escolha' : mode === 'logo' ? 'Usar esta imagem' : 'Escolher esta foto';

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      aria-labelledby="media-preview-title"
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' && canPrev) onStep(-1);
        if (e.key === 'ArrowRight' && canNext) onStep(1);
      }}
      PaperProps={{ sx: { ...DIALOG_PAPER_SX, ...(fullScreen ? { borderRadius: 0, border: 0 } : {}), overflow: 'hidden' } }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' }, minHeight: 0, height: fullScreen ? '100%' : 'auto', overflow: 'auto' }}>
        {/* palco */}
        <Box sx={{ position: 'relative', bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1.5, md: 3 }, minHeight: { xs: 240, md: 520 } }}>
          <Box
            sx={{
              position: 'relative', width: { xs: `min(100%, calc(52vh * ${r}))`, md: `min(100%, calc(72vh * ${r}))` },
              aspectRatio: String(r), bgcolor: item.color || 'var(--divider)', borderRadius: 'var(--radius-sm)', overflow: 'hidden',
              boxShadow: 'var(--shadow-hover)',
            }}
          >
            <Box key={item.id} component="img" src={item.previewUrl} alt={item.alt || item.name || ''} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', animation: `${rise} .25s ease-out`, ...REDUCED }} />
            {item.selected && <Box sx={{ position: 'absolute', top: 10, left: 10 }}><OrderBadge index={item.selectionIndex} size={36} /></Box>}
          </Box>
          {canPrev && (
            <IconButton aria-label="Foto anterior" onClick={() => onStep(-1)} sx={{ ...ON_PHOTO_BTN_SX, position: 'absolute', left: { xs: 8, md: 12 }, top: '50%', transform: 'translateY(-50%)' }}>
              <ChevronLeftRounded />
            </IconButton>
          )}
          {canNext && (
            <IconButton aria-label="Próxima foto" onClick={() => onStep(1)} sx={{ ...ON_PHOTO_BTN_SX, position: 'absolute', right: { xs: 8, md: 12 }, top: '50%', transform: 'translateY(-50%)' }}>
              <ChevronRightRounded />
            </IconButton>
          )}
          {fullScreen && (
            <IconButton aria-label="Fechar foto ampliada" onClick={onClose} sx={{ ...ON_PHOTO_BTN_SX, position: 'absolute', top: 8, right: 8 }}>
              <CloseRounded />
            </IconButton>
          )}
        </Box>

        {/* painel */}
        <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.75, borderLeft: { md: '1px solid var(--divider)' }, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography id="media-preview-title" variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', overflowWrap: 'anywhere', lineHeight: 1.3 }}>
                {title}
              </Typography>
              {item.alt && item.name && (
                <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{item.name}</Typography>
              )}
            </Box>
            {!fullScreen && (
              <IconButton aria-label="Fechar foto ampliada" onClick={onClose} sx={{ ...ICON_BTN_SX, mt: -0.75, mr: -0.75 }}>
                <CloseRounded />
              </IconButton>
            )}
          </Box>

          {(item.badgeLabel || item.usedInLabel || item.addedLabel) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {item.badgeLabel && <InfoChip tone="brand" icon={<AutoAwesomeRounded sx={{ fontSize: 14 }} />} label={item.badgeLabel} />}
              {item.addedLabel && <InfoChip tone="success" icon={<CheckRounded sx={{ fontSize: 14 }} />} label={item.addedLabel} />}
              {item.usedInLabel && <InfoChip icon={<LayersRounded sx={{ fontSize: 14 }} />} label={item.usedInLabel} />}
            </Box>
          )}

          {item.creditLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--text-secondary)' }}>
              <PhotoCameraRounded sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>{item.creditLabel}</Typography>
            </Box>
          )}

          {item.canDescribe && (
            <Box sx={{ bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', borderRadius: 'var(--radius-sm)', p: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, mb: 0.5 }}>
                <AccessibilityNewRounded sx={{ fontSize: 16, color: 'var(--text-secondary)' }} />
                <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)' }}>Descrição para leitor de tela</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: item.alt ? 'var(--text-primary)' : 'var(--text-secondary)', fontStyle: item.alt ? 'normal' : 'italic', overflowWrap: 'anywhere' }}>
                {item.alt || 'Sem descrição ainda.'}
              </Typography>
              <Button onClick={() => a.onOpenDescribe?.(item.id)} startIcon={<EditNoteRounded sx={{ fontSize: 18 }} />} sx={{ ...QUIET_BTN_SX, color: 'var(--color-primary-dark)', px: 1, ml: -1, mt: 0.5, minHeight: 40 }}>
                {item.alt ? 'Editar descrição' : 'Descrever'}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: { md: 'auto' }, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Button
              onClick={() => a.onToggleSelect?.(item.id)}
              disabled={!item.selectable && !item.selected}
              startIcon={item.selected ? <CloseRounded /> : <CheckRounded />}
              disableElevation
              sx={item.selected ? { ...OUTLINE_BTN_SX, minHeight: 44, width: '100%' } : { ...PRIMARY_BTN_SX, width: '100%' }}
            >
              {selectLabel}
            </Button>
            {item.canDelete && (
              <Button onClick={() => a.onAskDelete?.(item.id)} startIcon={<DeleteOutlineRounded sx={{ fontSize: 18 }} />} sx={DANGER_QUIET_BTN_SX}>
                Apagar da biblioteca
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DIÁLOGOS — descrever e apagar (erro DENTRO do diálogo)
   ═══════════════════════════════════════════════════════════════════════════ */

function DialogThumb({ url, size = 72 }) {
  if (!url) return null;
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, borderRadius: 'var(--radius-sm)', overflow: 'hidden', bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)' }}>
      <Box component="img" src={url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </Box>
  );
}

function DialogError({ error }) {
  if (!error) return null;
  return (
    <NoteRow tone="error" role="alert" icon={<ErrorRounded sx={{ fontSize: 20 }} />} sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{error}</Typography>
    </NoteRow>
  );
}

function DescribeDialog({ d, a }) {
  return (
    <Dialog open={!!d?.open} onClose={() => { if (!d.saving) a.onCloseDescribe?.(); }} maxWidth="sm" fullWidth aria-labelledby="media-describe-title" PaperProps={{ sx: DIALOG_PAPER_SX }}>
      <DialogTitle id="media-describe-title" sx={{ px: 3, pt: 3, pb: 1, ...HEAD, color: 'var(--text-primary)' }}>Descrever a foto</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
          <DialogThumb url={d?.thumbnailUrl} size={96} />
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <TextField
              autoFocus fullWidth multiline minRows={3}
              value={d?.value ?? ''}
              onChange={(e) => a.onChangeDescription?.(e.target.value)}
              disabled={!!d?.saving}
              inputProps={{ maxLength: d?.maxLength, 'aria-label': 'Descrição da foto' }}
              sx={{ ...FIELD_SX, mt: 0.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'space-between', alignItems: 'flex-start', mt: 0.75 }}>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>{d?.helper}</Typography>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{d?.counterLabel}</Typography>
            </Box>
          </Box>
        </Box>
        <DialogError error={d?.errorMessage} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button onClick={() => a.onCloseDescribe?.()} disabled={!!d?.saving} sx={QUIET_BTN_SX}>Cancelar</Button>
        <Button
          onClick={() => a.onSaveDescription?.()}
          disabled={!!d?.saving}
          startIcon={d?.saving ? <CircularProgress size={16} sx={{ color: 'var(--on-primary)' }} /> : null}
          disableElevation sx={PRIMARY_BTN_SX}
        >
          {d?.saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({ d, a }) {
  const heavy = !!d?.inUse;
  return (
    <Dialog open={!!d?.open} onClose={() => { if (!d.deleting) a.onCloseDelete?.(); }} maxWidth="xs" fullWidth aria-labelledby="media-delete-title" PaperProps={{ sx: { ...DIALOG_PAPER_SX, ...(heavy ? { borderColor: 'var(--tint-error-border)' } : {}) } }}>
      <DialogTitle id="media-delete-title" sx={{ px: 3, pt: 3, pb: 1.5, ...HEAD, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
        {heavy && <WarningAmberRounded sx={{ color: 'var(--color-error)' }} />}
        {d?.title}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'center' }}>
          <Box sx={{ position: 'relative' }}>
            <DialogThumb url={d?.thumbnailUrl} size={84} />
            {heavy && (
              <Box sx={{ position: 'absolute', right: -8, bottom: -8, width: 30, height: 30, borderRadius: '50%', bgcolor: 'var(--surface)', boxShadow: 'var(--shadow-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LayersRounded sx={{ fontSize: 18, color: 'var(--color-error)' }} />
              </Box>
            )}
          </Box>
          {heavy ? (
            <NoteRow tone="error" icon={<WarningAmberRounded sx={{ fontSize: 20 }} />} sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ ...HEAD, lineHeight: 1.45 }}>{d?.message}</Typography>
            </NoteRow>
          ) : (
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>{d?.message}</Typography>
          )}
        </Box>
        <DialogError error={d?.errorMessage} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={() => a.onCloseDelete?.()} disabled={!!d?.deleting} sx={heavy ? OUTLINE_BTN_SX : QUIET_BTN_SX}>Cancelar</Button>
        <Button
          onClick={() => a.onConfirmDelete?.()}
          disabled={!!d?.deleting}
          startIcon={d?.deleting ? <CircularProgress size={16} sx={{ color: 'var(--tint-error-sub)' }} /> : <DeleteOutlineRounded />}
          disableElevation sx={DANGER_BTN_SX}
        >
          {d?.deleting ? 'Apagando…' : d?.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   View
   ═══════════════════════════════════════════════════════════════════════════ */

export default function MediaLibraryView({ vm, actions }) {
  const a = actions || {};
  const [previewId, setPreviewId] = useState(null); // UI: foto ampliada
  const [dragging, setDragging] = useState(false);  // UI: arquivo passando por cima da janela
  const dragDepth = useRef(0);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const examplesRef = useRef(null);

  if (!vm) return null;

  const fullScreen = !!vm.fullScreen;
  const upload = vm.upload || {};
  const sections = vm.sections || [];
  const owner = sections.find((s) => s.key === 'owner');
  const examples = sections.find((s) => s.key === 'examples');
  const allItems = sections.flatMap((s) => s.items || []);
  const chosen = allItems.filter((i) => i.selected).sort((x, y) => (x.selectionIndex ?? 0) - (y.selectionIndex ?? 0));
  const previewIndex = allItems.findIndex((i) => i.id === previewId);
  const sel = vm.selection || {};
  const ready = vm.state === 'ready';

  const pick = () => { if (upload.canUpload) fileRef.current?.click(); };
  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) a.onUploadFiles?.(files);
    e.target.value = '';
  };
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const dnd = ready ? {
    onDragEnter: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); },
    onDragOver: (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = upload.canUpload ? 'copy' : 'none'; },
    onDragLeave: (e) => { if (!hasFiles(e)) return; dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); dragDepth.current = 0; setDragging(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (upload.canUpload && files.length) a.onUploadFiles?.(files);
    },
  } : {};

  const seeExamples = () => {
    const box = scrollRef.current; const target = examplesRef.current;
    if (box && target) box.scrollTo({ top: target.offsetTop - 16, behavior: 'smooth' });
  };

  const renderSection = (section) => {
    const isOwner = section.key === 'owner';
    const items = section.items || [];
    if (isOwner && items.length === 0) {
      return (
        <Box component="section" key={section.key}>
          <SectionHead section={section} />
          <EmptyOwner
            section={section} upload={upload} onPick={pick}
            examplesTitle={examples?.items?.length ? examples.title : null}
            onSeeExamples={seeExamples}
          />
        </Box>
      );
    }
    return (
      <Box component="section" key={section.key} ref={isOwner ? undefined : examplesRef}>
        <SectionHead section={section} />
        {items.length === 0 && section.emptyMessage ? (
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{section.emptyMessage}</Typography>
        ) : (
          <JustifiedRow>
            {isOwner && <UploadTile upload={upload} onPick={pick} />}
            {items.map((it) => (
              <PhotoTile key={it.id} item={it} onToggle={(id) => a.onToggleSelect?.(id)} onExpand={setPreviewId} />
            ))}
          </JustifiedRow>
        )}
      </Box>
    );
  };

  let body;
  if (vm.state === 'loading') {
    body = <LoadingSkeleton />;
  } else if (vm.state === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title="Não foi possível abrir as fotos"
        description={vm.errorMessage}
        action={(
          <Button onClick={() => a.onRetry?.()} startIcon={<ReplayRounded />} sx={{ ...BRAND_OUTLINE_BTN_SX, minHeight: 44 }}>
            Tentar novamente
          </Button>
        )}
      />
    );
  } else {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, sm: 4 } }}>
        <UploadTray uploads={vm.uploads} a={a} />
        {owner && renderSection(owner)}
        {examples && renderSection(examples)}
        {vm.hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => a.onLoadMore?.()}
              disabled={!!vm.loadingMore}
              startIcon={vm.loadingMore ? <CircularProgress size={16} sx={{ color: 'var(--text-secondary)' }} /> : <ExpandMoreRounded />}
              sx={{ ...OUTLINE_BTN_SX, minHeight: 44, px: 3 }}
            >
              {vm.loadingMore ? 'Carregando…' : 'Mostrar mais fotos'}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <Dialog
        open={!!vm.open}
        onClose={() => a.onClose?.()}
        fullScreen={fullScreen}
        maxWidth={false}
        aria-labelledby="media-library-title"
        PaperProps={{
          sx: {
            ...DIALOG_PAPER_SX,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            ...(fullScreen
              ? { borderRadius: 0, border: 0, m: 0 }
              : { width: 'calc(100% - 48px)', maxWidth: 1200, height: 'calc(100% - 48px)', maxHeight: 'none', m: 3 }),
          },
        }}
      >
        <input ref={fileRef} type="file" hidden accept={upload.accept} multiple={vm.mode !== 'logo'} onChange={onFiles} />

        {/* ── cabeçalho fixo ── */}
        <Box
          component="header"
          sx={{
            flexShrink: 0, px: { xs: 1.5, sm: 3 }, py: { xs: 1.25, sm: 2 },
            borderBottom: '1px solid', borderColor: 'var(--divider)',
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1.25,
          }}
        >
          {fullScreen && (
            <IconButton aria-label="Voltar ao editor" onClick={() => a.onClose?.()} sx={{ ...ICON_BTN_SX, order: 0, ml: -0.5 }}>
              <ArrowBackRounded />
            </IconButton>
          )}
          <Box sx={{ flex: '1 1 0', minWidth: 180, order: 1 }}>
            <Typography id="media-library-title" variant="h6" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1.2 }}>
              {vm.title}
            </Typography>
            {vm.subtitle && (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.25, textWrap: 'pretty' }}>{vm.subtitle}</Typography>
            )}
          </Box>
          {!fullScreen && (
            <IconButton aria-label="Fechar biblioteca" onClick={() => a.onClose?.()} sx={{ ...ICON_BTN_SX, order: { xs: 2, md: 4 } }}>
              <CloseRounded />
            </IconButton>
          )}
          {ready && (
            <Box sx={{ order: 3, flexBasis: { xs: '100%', md: 'auto' }, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
              <QuotaMeter quota={vm.quota} />
              <UploadButton upload={upload} onPick={pick} />
            </Box>
          )}
        </Box>

        {/* ── fotos (rola) + camada de "solte aqui" ── */}
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }} {...dnd}>
          <Box ref={scrollRef} sx={{ position: 'absolute', inset: 0, overflowY: 'auto', px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
            {body}
          </Box>
          {dragging && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute', inset: { xs: 8, sm: 16 }, zIndex: 2, pointerEvents: 'none',
                borderRadius: 'var(--radius-card)', border: '3px dashed',
                borderColor: upload.canUpload ? 'var(--color-primary)' : 'var(--tint-warning-border)',
                bgcolor: upload.canUpload ? 'color-mix(in srgb, var(--surface) 82%, transparent)' : 'color-mix(in srgb, var(--tint-warning-bg) 92%, transparent)',
                backdropFilter: 'blur(3px)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, textAlign: 'center', p: 3,
                animation: `${rise} .15s ease-out`, ...REDUCED,
              }}
            >
              {upload.canUpload ? (
                <>
                  <Box sx={{ width: 88, height: 88, borderRadius: '50%', bgcolor: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-hover)' }}>
                    <CloudUploadRounded sx={{ fontSize: 44, color: 'var(--on-primary)' }} />
                  </Box>
                  <Typography variant="h6" component="p" sx={{ ...HEAD, color: 'var(--text-primary)' }}>Solte para enviar</Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{upload.acceptLabel}</Typography>
                </>
              ) : (
                <>
                  <LockOutlined sx={{ fontSize: 40, color: 'var(--tint-warning-fg)' }} />
                  <Typography variant="body1" sx={{ ...HEAD, color: 'var(--tint-warning-fg)', maxWidth: 420 }}>{upload.blockedReason}</Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* ── rodapé fixo: a escolha + confirmar ── */}
        <Box
          component="footer"
          sx={{
            flexShrink: 0, px: { xs: 1.5, sm: 3 }, py: { xs: 1.25, sm: 1.5 },
            pb: fullScreen ? 'max(12px, env(safe-area-inset-bottom))' : undefined,
            borderTop: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--surface)',
            display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 2 },
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ minWidth: 0, flexShrink: 0, maxWidth: { xs: '100%', sm: 260 } }}>
              <Typography variant="subtitle2" component="p" aria-live="polite" sx={{ ...HEAD, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {sel.label}
              </Typography>
              {sel.limitLabel && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {sel.limitReached && <InfoOutlined sx={{ fontSize: 14, color: 'var(--tint-warning-fg)' }} />}
                  <Typography variant="caption" sx={{ color: sel.limitReached ? 'var(--tint-warning-fg)' : 'var(--text-secondary)', fontWeight: sel.limitReached ? 'var(--fw-medium)' : 'var(--fw-regular)' }}>
                    {sel.limitLabel}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, display: { xs: 'none', sm: 'block' } }}>
              <SelectionStrip items={chosen} onToggle={(id) => a.onToggleSelect?.(id)} />
            </Box>
          </Box>
          <Box sx={{ display: { xs: chosen.length ? 'block' : 'none', sm: 'none' }, width: '100%', order: -1 }}>
            <SelectionStrip items={chosen} onToggle={(id) => a.onToggleSelect?.(id)} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: 'auto' }}>
            {!fullScreen && <Button onClick={() => a.onClose?.()} sx={QUIET_BTN_SX}>Cancelar</Button>}
            <Button
              onClick={() => a.onConfirm?.()}
              disabled={!sel.canConfirm || !ready}
              startIcon={<CheckRounded />}
              disableElevation
              sx={{ ...PRIMARY_BTN_SX, minHeight: 46, px: 2.75 }}
            >
              {sel.confirmLabel}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {vm.open && previewIndex >= 0 && (
        <Lightbox
          items={allItems}
          index={previewIndex}
          mode={vm.mode}
          fullScreen={fullScreen}
          onClose={() => setPreviewId(null)}
          onStep={(d) => { const next = allItems[previewIndex + d]; if (next) setPreviewId(next.id); }}
          a={a}
        />
      )}

      {vm.open && <DescribeDialog d={vm.describeDialog || {}} a={a} />}
      {vm.open && <DeleteDialog d={vm.deleteDialog || {}} a={a} />}

      {/* O aviso efêmero (`vm.notice`: "Foto apagada.", "Descrição salva.") NÃO mora aqui: o projeto padroniza
          toda confirmação no `AppSnackbar` e o eslint proíbe `Snackbar` cru para as telas não divergirem — mesmo
          caso do PagesView. Quem desenha é o container (`media/MediaLibrary.jsx`), acima do rodapé desta janela.
          Ajuste autorizado pelo André em 25/10 ("pode tocar e ajustar o problema de lint"). */}
    </>
  );
}
